import { Div, Line, NumberInput, Select, Span, TableBuilder, TextInput } from "@tblabs/truffle";
import { PackageToCustomerStepState } from "./Models/PackageToCustomerStepState";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { TimeUtils } from "../../Utils/TimeUtils";
import moment from "moment";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { DateSlider } from "../../Components/Utils/DateSlider";
import { StepType } from "../Step/StepType";
import { ToDo } from "../../Components/ToDo";
import { PackageToCustomerStepData } from "./Models/PackageToCustomerStepData";
import { PackageTracker } from "./Components/PackageTracker";
import { BasketCollectionPrinter } from "./Components/BasketCollectionPrinter";
import { CashType } from "../Step/CashType";


export class PackageToCustomerStep extends Step<PackageToCustomerStepState>
{
    public Type = StepType.PackageToCustomer;
    public Icon = "📦";
    public TitleForCustomer = () => `Wysyłka`;
    public Title = () => `Wysyłka (${this.Data.Carrier.value || "Pocztex"})`;

    public InitialState = PackageToCustomerStepState.NotPlannedYet;
    public WellDoneStates = [PackageToCustomerStepState.Delivered];
    public FailedDoneStates = [PackageToCustomerStepState.Cancelled, PackageToCustomerStepState.RejectedByCustomer, PackageToCustomerStepState.PackageLost];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.PackageToCustomer, this.Order.Id, "📦", "Wysyłka", this.Data.PlannedDispatchTime.value, this.Data.State.Is(PackageToCustomerStepState.InTransit))
        ];
    }
    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if (this.Data.PackageCost.value > 0
            && (this.Data.State.Is(PackageToCustomerStepState.InTransit) || this.IsDone))
        {
            cash.push(new Cash(CashType.Expense, this, this.Data.PackageCost.value, "Koszt paczki", this.Data.DispatchTime.value))
        }

        return cash;
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedDispatchTime.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.DispatchTime.value;
    }

    constructor(public Data: PackageToCustomerStepData)
    {
        super(Data);

        this.Data.SearchBy = this.Data.PackageNumber.value + " " + this.Data.PackageContent.Items.join(" ");
    }

    public STATES = {
        [PackageToCustomerStepState.NotPlannedYet]: {
            StateName: "Jeszcze nie zaplanowane",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [`Wysyłka nie została jeszcze zaplanowana`],
            },
            Admin: {
                Content: () => [`Czas wysyłki nie został jeszcze zaplanowany`, new ToDo(`Ustal czas wysyłki`, new DateSlider(this.Data.PlannedDispatchTime))],
                Actions: {
                    "✅": () => this.GoTo(PackageToCustomerStepState.Planned),
                    "❌": () => this.GoTo(PackageToCustomerStepState.Cancelled),
                },
            },
        },
        [PackageToCustomerStepState.Planned]: {
            StateName: "Wysyłka " + TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime, "(nie określono)", false),
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    `✔️ Wysyłka zaplanowana na ${TimeUtils.FormattedDate(this.Data.PlannedDispatchTime)}\n`,
                    this.Data.PaymentAtDelivery.value > 0 && `\nKwota pobrania: `,
                    this.Data.PaymentAtDelivery.value > 0 && new Span(this.Data.PaymentAtDelivery.value + " zł").Attribute("data-testid", "package-payment-info-amount")
                ],
            },
            Admin: {
                Content: () => [
                    `✔️ Wysyłka ${TimeUtils.TimeLeftTo(this.Data.PlannedDispatchTime)} (${TimeUtils.FormattedDate(this.Data.PlannedDispatchTime)}).`,
                    new BasketCollectionPrinter(this.Order, this.Data.PackageContent),
                    new Div("meeting-info-entry").Text(this.Data.AddressFullInfo.value || "(brak adresu)"),
                    `Pobranie: ${this.Data.PaymentAtDelivery.value + " zł"} `,
                    new ToDo(`Przygotuj etykietę na podstawie powyższych danych.\nNumer przesyłki i numery seryjne urządzeń będą do wpisania w następnym kroku`),
                ],
                Actions: {
                    "✅": () => this.GoTo(PackageToCustomerStepState.LabelReady),
                    "❌": () => this.GoTo(PackageToCustomerStepState.NotPlannedYet),
                }
            }
        },
        [PackageToCustomerStepState.LabelReady]: {
            StateName: "Etykieta gotowa",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    `☑️ Paczka w przygotowaniu...`],
            },
            Admin: {
                Content: () => [
                    `☑️ Etykieta została wypełniona danymi klienta`,
                    new ToDo(`Wpisz numer paczki oraz numery seryjne wysyłanych urządzeń\n\n`,
                        "Przewoźnik", new Select(this.Data.Carrier, ["Pocztex", "Inpost", "DHL", "DPD", "Uber"]),
                        "\n\nNumer paczki", new TextInput(this.Data.PackageNumber),
                        "\n\nKoszt paczki", new NumberInput(this.Data.PackageCost).Min(0).MarginLeftRight(8), "zł",
                        new TableBuilder(this.Order.BasketItems)
                            .AddColumn("Produkt", x => x.ProductInfo.Name + " ×" + x.Quantity.value)
                            .AddColumn("Numer seryjny", x => new TextInput(x.ProductSerialNumber))
                            .AddColumn("Dane dostępu", x => new TextInput(x.AccessData))
                            .Build()),
                ],
                Actions: {
                    "✅": () => this.GoTo(PackageToCustomerStepState.ReadyToShipment),
                    "❌": () => this.GoTo(PackageToCustomerStepState.Planned),
                }
            }
        },
        [PackageToCustomerStepState.ReadyToShipment]: {
            StateName: "Gotowa do wysłania",
            Timeout: () => this.Data.PlannedDispatchTime,
            Customer: {
                Content: () => [
                    `☑️ Twoja paczka jest już gotowa do wysłania. Nadamy ją ${TimeUtils.FormattedDate(this.Data.PlannedDispatchTime)}`,
                    this.Data.PaymentAtDelivery.value && `, za pobraniem ${this.Data.PaymentAtDelivery.value} zł`],
            },
            Admin: {
                Content: () => [
                    `☑️ Etykieta "${this.Data.PackageNumber.value || "(brak numeru paczki)"}" ("${this.Data.Carrier.value}") gotowa\n`,
                    new ToDo(`Wyślij paczkę najdalej do ${moment(this.Data.PlannedDispatchTime.value).format("DD.MM.YYYY")} do godziny 15:00`)],
                Actions: {
                    "✅": () =>
                    {
                        this.Data.DispatchTime.value = DateTimeProvider.Now;
                        this.GoTo(PackageToCustomerStepState.InTransit);
                    },
                    "❌": () => this.GoTo(PackageToCustomerStepState.NotPlannedYet),
                }
            },
        },
        [PackageToCustomerStepState.InTransit]: {
            StateName: "W trasie",
            Timeout: () => moment(this.Data.DispatchTime.value).add(2, "days").toDate(),
            Customer: {
                Content: () => [`🚚 Paczka jest już w drodze\n\nPowinna dotrzeć w ciągu 24-48h\n\n`, new PackageTracker(this.Data)],
            },
            Admin: {
                Content: () => [
                    `Paczka nadana ${TimeUtils.FormattedDate(this.Data.DispatchTime)} ✅\nJest w trasie od ${TimeUtils.Since(this.Data.DispatchTime)}`,
                    new ToDo(
                        `Sprawdź czy klient odebrał paczkę. Jeśli jest awizowana na poczcie wybierz 🏤`),
                    new PackageTracker(this.Data)],
                Actions: {
                    "✅": () => this.GoTo(PackageToCustomerStepState.Delivered),
                    "🏤": () => { this.Data.LastStatusCheckTime.value = DateTimeProvider.Now; this.GoTo(PackageToCustomerStepState.AwaitingInPostOffice); },
                    "❌": () => this.GoTo(PackageToCustomerStepState.NotDeliveredYet),
                }
            }
        },
        [PackageToCustomerStepState.NotDeliveredYet]: {
            StateName: "Nie dostarczona",
            Customer: {
                Content: () => [
                    `🚚 Paczka wciąż jest w drodze\n\nWiększość paczek dociera w ciągu 1-2 dni roboczych. Kliknij w link poniżej, aby dowiedzieć się więcej`,
                    new PackageTracker(this.Data)
                ],
            },
            Admin: {
                Content: () => [
                    `✖️ Minęło już ${moment(this.Data.DispatchTime.value).from(moment(DateTimeProvider.Now))} od nadania`,
                    new ToDo(
                        `Warto sprawdzić co się dzieje z paczką`,
                        new PackageTracker(this.Data))
                ],
                Actions: {
                    "✅": () => this.GoTo(PackageToCustomerStepState.Delivered),
                    "❌": () => this.GoTo(PackageToCustomerStepState.Problem),
                }
            }
        },
        [PackageToCustomerStepState.Problem]: {
            StateName: "Problem z dostawą",
            Customer: {
                Content: () => [`✖️ Jest jakiś problem z dostawą...`],
            },
            Admin: {
                Content: () => [`✖️ Problem z dostawą`, new ToDo(`Określ rodzaj problemu`)],
                Actions: {
                    "🏤 Awizowana": () => this.GoTo(PackageToCustomerStepState.AwaitingInPostOffice),
                    "❌ Nie odebrana": () => 
                    {
                        this.Data.LastStatusCheckTime.value = DateTimeProvider.Now;
                        this.GoTo(PackageToCustomerStepState.RejectedByCustomer);
                    },
                    "🌊 Zgubiona": () => this.GoTo(PackageToCustomerStepState.PackageLost),
                    "🚚 Ciągle w drodze": () =>
                    {
                        this.Data.LastStatusCheckTime.value = DateTimeProvider.Now;
                        this.GoTo(PackageToCustomerStepState.StillOnTheWay);
                    },
                    "🔙 Zwrócona": () => this.GoTo(PackageToCustomerStepState.PackageReturned),
                }
            }
        },
        [PackageToCustomerStepState.Delivered]: {
            StateName: "Dostarczona",
            Customer: {
                Content: () => [`✅ Paczka dostarczona`],
            },
            Admin: {
                Content: () => [
                    `✅ Paczka dostarczona`,
                    new BasketCollectionPrinter(this.Order, this.Data.PackageContent),
                    new Line(),
                    `Number paczki: ${this.Data.PackageNumber.value} (${this.Data.Carrier.value})`
                ],
            }
        },
        [PackageToCustomerStepState.RejectedByCustomer]: {
            StateName: "Zrezygnowano z odebrania",
            Timeout: () => moment(this.Data.LastStatusCheckTime.value).add(7, "days").toDate(),
            Customer: {
                Content: () => [`❌ Zrezygnowano z odebrania paczki`,
                    new Line(),
                    `Paczkę wciąż można odebrać na poczcie w przeciągu 7 dni od daty jej dostarczenia do urzędu. Po tym czasie zostanie zwrócona do nadawcy`],
            },
            Admin: {
                Content: () => [
                    `❌ Klient świadomie nie odebrał paczki`,
                    new ToDo(`Oczekuj zwrotu paczki. Anuluj zwrot kaucji.\nKlient wciąż może odebrać paczkę w urzędzie przez 7 dni (i taki widzi komunikat)`),
                    new PackageTracker(this.Data),
                ],
            }
        },
        [PackageToCustomerStepState.StillOnTheWay]: {
            StateName: "Ciągle w drodze",
            Timeout: () => moment(this.Data.LastStatusCheckTime.value).add(1, "days").toDate(),
            Customer: {
                Content: () => [`Paczka jest w drodze dłużej niż zakładano...`],
            },
            Admin: {
                Content: () => [
                    `Paczka wciąż jest w drodze a powinna już dotrzeć`,
                    new ToDo(`Skontaktuj się z przewoźnikiem i zapytaj o co chodzi`),
                    new PackageTracker(this.Data),
                ],
            }
        },
        [PackageToCustomerStepState.PackageLost]: {
            StateName: "Zgubiona",
            Customer: {
                Content: () => [`❌ Paczka została zgubiona`],
            },
            Admin: {
                Content: () => [
                    `❌ Paczka została najprawdopodobniej zgubiona`,
                    new ToDo(`Skontaktuj się z przewoźnikiem i złóż reklamację`),
                ],
            }
        },
        [PackageToCustomerStepState.AwaitingInPostOffice]: {
            StateName: "Awizowana",
            Timeout: () => moment(this.Data.LastStatusCheckTime.value).add(7, "days").toDate(),
            Customer: {
                Content: () => [`🏤 Paczka jest awizowana na poczcie. Zgłoś się po nią do placówki przydzielonej dla Twojego adresu zamieszkania w ciągu 7 dni. Nie zapomnij zabrać ze sobą dowodu osobistego`],
            },
            Admin: {
                Content: () => [`🏤 Paczka awizowana`, new ToDo(`Upewnij się, że klient wie co to znaczy i co ma robić`)],
            }
        },
        [PackageToCustomerStepState.Cancelled]: {
            StateName: "Odwołana",
            Customer: {
                Content: () => [`❌ Wysyłka została odwołana`],
            },
            Admin: {
                Content: () => [`❌ Wysyłka została odwołana`],
            }
        },
        [PackageToCustomerStepState.PackageReturned]: {
            StateName: "Zwrócona",
            Customer: {
                Content: () => [`✖️ Przesyłka została zwrócona`],
            },
            Admin: {
                Content: () => [`✖️ Przesyłka została zwrócona\n`,
                    new PackageTracker(this.Data),
                    new ToDo(`Warto ustalić dlaczego paczka się odbiła. Należy anulować kolejne kroki i zastanowić się co zrobić z kaucją (zależy czy wina klienta czy nie)`)],
            }
        },
    };
}
